import {
    CurrentConfiguration,
    CurrentConfigurationExtendedwarranty,
    CurrentConfigurationFullservice,
    CurrentConfigurationLeasing,
    CurrentConfigurationRent,
    ConfiguratorProduct,
    ConfiguratorProductBuckets,
    ConfiguratorProductCategories,
    ConfiguratorProductOptions,
    ConfiguratorProductPrice,
    ConfiguratorProductTypes,
    ConfiguratorProductQuickcouplers1,
    ConfiguratorProductQuickcouplers,
    ConfiguratorProductSpecialdiscount, AvescoContact
} from "App/services/api";
import {extractPrice, extractPrice1, FetchAction, FetchState} from "App/services";
import {
    ActionType, ContactType,
    Currency, CURRENT_CURRENCY,
    DIVISION_ROUNDING_THRESHOLD, Language, NATURAL_SORT_COMPARE_OPTIONS,
    ROUND_BRUTTO_TO_NEAREST_SMALL_AMOUNTS,
    ROUND_NETTO_TO_NEAREST_DEFAULT
} from "App/constants";
import {Map, List} from 'immutable';
import {ThunkDispatch} from "redux-thunk";
import {AnyAction} from "redux";
import {ApplicationState} from "App/store";
import {validateOrder} from "App/routes";
import * as md5 from "md5";
import {buildCustomerContact, ContactData} from "App/contactData";

export interface SyncState {
    version: string|null
    pending: boolean
    failed: boolean
}

// Basket for a single configuration
export interface Basket {
    /**
     */
    fullservice?: CurrentConfigurationFullservice
    /*
     */
    extendedwarranty?: CurrentConfigurationExtendedwarranty
    /**
     * maps the category id to the selected options id
     */
    options: Map<number,List<number>>
    /**
     * The effective discount
     */
    discount: number
    /**
     * the price for the current configuration. Filled by the server
     */
    price?: number
    /**
     * the customer identifier. This is the DBS CALPF001.CUNO field. Filled by the server.
     */
    customerid?: string
    /**
     * the id of the offer (matches MoSale Purchase). Can be null if the offer was not yet saved.
     */
    purchaseid?: number
    /**
     * the id of the productform (matches MoSale Purchase). Can be null if the offer was not yet saved.
     */
    productformid?: number
    /**
     * the Id of the product
     */
    productid?: number
    /**
     * the id of the machine (typ)
     */
    machineid?: number
    /**
     */
    rent?: CurrentConfigurationRent
    /**
     */
    leasing?: CurrentConfigurationLeasing
    /**
     * The cached options
     */
    cachedOptions: Map<string,ConfiguratorProductOptions>
    /**
     * The chosen baseprice
     */
    basePrice?: ConfiguratorProductPrice
    /**
     * Round to nearest for division
     */
    divisionroundtonearest?: number
    /**
     * The defined standarddiscount
     */
    standarddiscount?: number
    /**
     * The defined specialdiscount
     */
    specialdiscount?: ConfiguratorProductSpecialdiscount;
    /**
     * The QuickCoupler
     */
    quickCouplerSystemId?: number
    quickCouplerModelId?: number
    quickCouplerModelPrice?: ConfiguratorProductPrice
    quickCouplerBuckets: Array<ConfiguratorProductBuckets>
    /**
     * The standard price (computed)
     */
    standardprice?: number
    /*
     * The total price (computed or server)
     */
    totalprice?: number
    /*
     * Sync with server
     */
    syncState: SyncState
    /*
     * Salesman
     */
    salesman?: AvescoContact
}

export const initialState: Basket = {
    options: Map<number, List<number>>(),
    cachedOptions: Map<string, ConfiguratorProductOptions>(),
    quickCouplerBuckets: [],
    discount: 0,
    syncState: {
        version: null,
        pending: false,
        failed: false
    }
};

function selectInitialProductType(types: Array<ConfiguratorProductTypes>): ConfiguratorProductTypes|undefined {
    if (types&&types.length>=1) {
        return types
            .sort((t1, t2) => (t1.name||'').localeCompare(t2.name||'', undefined, NATURAL_SORT_COMPARE_OPTIONS) ||
                (t1.linkagename||'').localeCompare(t2.linkagename||'', undefined, NATURAL_SORT_COMPARE_OPTIONS))[0];
    }
    return undefined;
}

export function reducer(state: Basket = initialState, action: FetchAction | BasketAction) {
    switch (action.type) {
        case ActionType.PAGE_MAIN:
        case ActionType.PAGE_CATEGORY:
        case ActionType.PAGE_SUBCATEGORY:
        case ActionType.PAGE_SPECIALOFFER: {
            return initialState;
        }
        case ActionType.FETCH_CONFIGURATORPRODUCT: {
            switch (action.state) {
                case FetchState.SUCCESS: {
                    const conf: ConfiguratorProduct = action.payload;
                    const initialType: ConfiguratorProductTypes|undefined = selectInitialProductType(conf.types);
                    return updateTotalPrice({
                        ... initialState,
                        productid: conf.id,
                        machineid: initialType?initialType.typeid:undefined,
                        basePrice: initialType?initialType.price:undefined,
                        divisionroundtonearest: conf.divisionroundtonearest,
                        standarddiscount: conf.standarddiscount,
                        specialdiscount: conf.specialdiscount&&{...conf.specialdiscount},
                        discount: (conf.specialdiscount?conf.specialdiscount.percentage:conf.standarddiscount),
                        salesman: conf.salesman
                    })
                }
                default:
                    return state;
            }
        }
        case ActionType.BASKET_CHANGEPRODUCTTYPE: {
            return updateTotalPrice({
                ...state,
                machineid: action.payload.type.typeid,
                basePrice: action.payload.type.price
            })
        }
        case ActionType.BASKET_CHANGEOPTIONSELECTION: {
            const selected: boolean = action.payload.selected;
            const category: ConfiguratorProductCategories = action.payload.category;
            const option: ConfiguratorProductOptions = action.payload.option;
            const singleSelectionPerCategory: boolean = category.singleselection;
            var options: Map<number,List<number>> =state.options;
            if (singleSelectionPerCategory) {
                options = options.delete(category.categoryid);
                if (selected) options = options.set(category.categoryid, List([option.optionid]));
            } else {
                var selections: List<number> =  options.get(category.categoryid) || List<number>();
                selections = selections.filter((value) => value != option.optionid);
                if (selected) selections = selections.push(option.optionid);
                if (selections.size>0) {
                    options = options.set(category.categoryid, selections);
                } else {
                    options = options.delete(category.categoryid);
                }
            }
            return updateTotalPrice({
                ...state,
                options,
                cachedOptions: selected?state.cachedOptions.set(category.categoryid + "-" + option.optionid, option):state.cachedOptions
            })
        }
        case ActionType.BASKET_CHANGELEASINGSELECTION: {
            let leasing: CurrentConfigurationLeasing | undefined = state.leasing?{...state.leasing}:undefined;
            leasing = action.payload.selected?{...leasing, durationmonths:action.payload.durationmonths, initialpaymentpercent:action.payload.initialpaymentpercent}:undefined;
            return {
                ...state,
                leasing: leasing
            }
        }
        case ActionType.BASKET_CHANGERENTSELECTION: {
            let rent: CurrentConfigurationRent | undefined = state.rent?{...state.rent}:undefined;
            rent = action.payload.selected?{...rent, durationmonths:action.payload.durationmonths}:undefined;
            return {
                ...state,
                rent: rent
            };
        }
        case ActionType.BASKET_CHANGEFULLSERVICESELECTION: {
            let fullService: CurrentConfigurationFullservice | undefined = state.fullservice?{...state.fullservice}:undefined;
            fullService = action.payload.selected?{...fullService, durationmonths:action.payload.durationmonths, hoursperyear:action.payload.hoursperyear}:undefined;
            return updateTotalPrice({
                ...state,
                fullservice: fullService
            })
        }
        case ActionType.BASKET_CHANGEEXTENDEDWARRANTYSELECTION: {
            let extendedWarranty: CurrentConfigurationExtendedwarranty | undefined = state.extendedwarranty?{...state.extendedwarranty}:undefined;
            extendedWarranty = action.payload.selected?{...extendedWarranty,
                durationmonths:action.payload.durationmonths||(extendedWarranty&&extendedWarranty.durationmonths),
                warrantytype:action.payload.warrantytype||(extendedWarranty&&extendedWarranty.warrantytype)}:undefined;
            return updateTotalPrice({
                ...state,
                extendedwarranty: extendedWarranty
            })
        }
        case ActionType.BASKET_CHANGEQUICKCOUPLERSELECTION: {
            var buckets: Array<ConfiguratorProductBuckets> = [];
            if (action.payload.selected!==undefined) {
                buckets = [...state.quickCouplerBuckets];
                buckets = buckets.filter((item:ConfiguratorProductBuckets) => item.bucketid != action.payload.bucket.bucketid);
                if (action.payload.selected) {
                    buckets.push(action.payload.bucket);
                }
            }
            return updateTotalPrice({
                ...state,
                quickCouplerSystemId: action.payload.systemId,
                quickCouplerModelId: action.payload.modelId,
                quickCouplerModelPrice: action.payload.modelPrice,
                quickCouplerBuckets: buckets
            })
        }
        case ActionType.BASKET_LOAD: {
            var configuration: CurrentConfiguration = action.payload.configuration;
            var product: ConfiguratorProduct = action.payload.product;
            var basket = fillBasket(configuration, product);
            return updateTotalPrice({
                ...state,
                ...basket
            })
        }
        case ActionType.CHECK_CONFIGURATION: {
            const conf: CurrentConfiguration = action.payload;

            switch (action.state) {
                case FetchState.FETCHING: {
                    const newState: Basket = {
                        ...state,
                        price: undefined
                    };
                    var configuration: CurrentConfiguration | undefined = buildConfiguration(newState, undefined, action.params.language, action.params.currency, undefined);
                    var currentVersion: string = computeConfigurationVersion(configuration);
                    return updateTotalPrice({
                        ...newState,
                        syncState: {
                            version: currentVersion,
                            pending: true,
                            failed: false
                        }
                    })
                }
                case FetchState.SUCCESS: {

                    var configuration: CurrentConfiguration | undefined = buildConfiguration(state, undefined, action.params.language, action.params.currency, undefined);
                    var currentVersion: string = computeConfigurationVersion(configuration);
                    return state.syncState.version===currentVersion?
                        updateTotalPrice({
                            ...state,
                            customerid: conf.customerid,
                            purchaseid: conf.purchaseid,
                            productformid: conf.productformid,
                            rent: conf.rent,
                            leasing: conf.leasing,
                            extendedwarranty: conf.extendedwarranty,
                            fullservice: conf.fullservice,
                            price: conf.price,
                            syncState: {
                                version: currentVersion,
                                pending: false,
                                failed: false
                            },
                            salesman: conf.salesman
                        }):
                        updateTotalPrice({
                            ...state,
                            customerid: conf.customerid,
                            purchaseid: conf.purchaseid,
                            productformid: conf.productformid,
                            rent: state.rent && {
                                ...state.rent,
                                monthlyrate: conf.rent?conf.rent.monthlyrate:state.rent.monthlyrate
                            },
                            leasing: state.leasing && {
                                ...state.leasing,
                                monthlyrate: conf.leasing?conf.leasing.monthlyrate:state.leasing.monthlyrate
                            },
                            extendedwarranty: state.extendedwarranty && {
                                ...state.extendedwarranty,
                                price: conf.extendedwarranty&&conf.extendedwarranty.price
                            },
                            fullservice: state.fullservice && {
                                ...state.fullservice,
                                monthlyrate: conf.fullservice&&conf.fullservice.monthlyrate
                            },
                            price: undefined,
                            salesman: conf.salesman
                        });
                }
                case FetchState.FAILURE: {
                    return {
                        ...state,
                        syncState: {
                            ...state.syncState,
                            pending: true,
                            failed: true
                        }
                    }
                }
                default:
                    return state;
            }
        }
        case ActionType.FETCH_CONFIGURATION: {
            const conf: CurrentConfiguration = action.payload;

            switch (action.state) {
                case FetchState.SUCCESS: {
                    return updateTotalPrice({
                        ...state,
                        customerid: conf.customerid,
                        purchaseid: conf.purchaseid,
                        productformid: conf.productformid,
                        rent: conf.rent,
                        leasing: conf.leasing,
                        extendedwarranty: conf.extendedwarranty,
                        fullservice: conf.fullservice,
                        price: conf.price
                    })
                }
                default:
                    return state;
            }
        }
        default:
            return state
    }
}

export function computeDiscount(price: number, discount: number, divisionroundtonearest?: number):number {
    // Add discount
    var discountPrice = (price * (1000000 - discount)) / 1000000;
    // Rounding
    if(discountPrice>DIVISION_ROUNDING_THRESHOLD) {
        const roundToNearest: number = divisionroundtonearest?divisionroundtonearest:ROUND_NETTO_TO_NEAREST_DEFAULT;
        return Math.round(discountPrice / roundToNearest) * roundToNearest;
    }else{
        return Math.round(discountPrice / ROUND_BRUTTO_TO_NEAREST_SMALL_AMOUNTS) * ROUND_BRUTTO_TO_NEAREST_SMALL_AMOUNTS;
    }
}

// Recompute the price and see if there is some difference
function updateTotalPrice(state: Basket): Basket {
    var currency = CURRENT_CURRENCY;
    // Set baseprice
    var basePrice: number | null = state.basePrice?extractPrice(state.basePrice, currency):0;
    // Add options price
    var price: number = state.options.reduce((reduction, value, key) => {
        return reduction + value.reduce((acc: number, id: number) => {
            var option: ConfiguratorProductOptions | undefined = state.cachedOptions.get(key + "-" + id);
            var optionPrice: number | null = option?extractPrice(option.price, currency):0;
            return acc + (optionPrice?optionPrice:0);
        }, 0)
    }, basePrice?basePrice:0);
    // Add quick coupler price
    var qcPrice: number | null = state.quickCouplerModelPrice?extractPrice(state.quickCouplerModelPrice, currency):null;
    price = price + (qcPrice?qcPrice:0);
    price = state.quickCouplerBuckets.reduce((acc:number, curr: ConfiguratorProductBuckets) => {
        return acc + (extractPrice1(curr.price, currency)||0);
    }, price);
    // No full service price
    // Add extended warranty price
    price = state.extendedwarranty&&state.extendedwarranty.price?price+state.extendedwarranty.price:price;
    // Add discount
    var discountPrice = computeDiscount(price, state.discount, state.divisionroundtonearest);
    var standardPrice = computeDiscount(price, state.standarddiscount||0, state.divisionroundtonearest);
    return {...state, standardprice: standardPrice, totalprice: state.price?state.price:discountPrice};
}

export function buildConfiguration(basket: Basket, form: ContactData | undefined, language: Language, currency: Currency, type: ContactType | undefined): CurrentConfiguration | undefined {
    return basket.productid&&basket.machineid?{
        quickcoupler: basket.quickCouplerModelId,
        buckets: basket.quickCouplerBuckets.map(b => b.bucketid),
        fullservice: basket.fullservice?{
            hoursperyear:basket.fullservice.hoursperyear,
            durationmonths:basket.fullservice.durationmonths
        }:undefined,
        extendedwarranty: basket.extendedwarranty?{
            warrantytype: basket.extendedwarranty.warrantytype,
            durationmonths: basket.extendedwarranty.durationmonths
        }:undefined,
        options: basket.options.reduce((reduction, value) => {
            // Merge and remove duplicates
            return [...new Set([...reduction,...value])];
        }, []),
        languagecode: language,
        currency: currency,
        purchaseid: basket.purchaseid,
        productformid: basket.productformid,
        productid: basket.productid,
        machineid: basket.machineid,
        rent: basket.rent?{
            durationmonths: basket.rent.durationmonths
        }:undefined,
        leasing: basket.leasing?{
            durationmonths: basket.leasing.durationmonths,
            initialpaymentpercent: basket.leasing.initialpaymentpercent
        }:undefined,
        customercontact: buildCustomerContact(form, type)
    }:undefined
}

export function fillBasket(configuration: CurrentConfiguration, product: ConfiguratorProduct): Basket {
    // build options list
    const options: Map<number,List<number>> = product.categories.reduce((map: Map<number,List<number>>, object:ConfiguratorProductCategories) => {
            const filteredOptions: Array<ConfiguratorProductOptions>|undefined = object.options&&object.options.filter((option) => configuration.options&&(<Array<number>>configuration.options).find((value) => value===option.optionid));
            if (filteredOptions&&filteredOptions.length) map = map.set(object.categoryid, List<number>(filteredOptions.map((option:ConfiguratorProductOptions) => option.optionid)));
            return map;
        }, Map<number,List<number>>());
    // build cached options
    const cachedOptions: Map<string,ConfiguratorProductOptions> = product.categories.reduce((map: Map<string,ConfiguratorProductOptions>, category:ConfiguratorProductCategories) => {
            category.options&&category.options.filter((option) => configuration.options&&(<Array<number>>configuration.options).find((value) => value===option.optionid))
                .reduce((map: Map<string,ConfiguratorProductOptions>, option:ConfiguratorProductOptions) => {
                    return map.set(category.categoryid + "-" + option.optionid, option);
                }, map);
            return map;
        }, Map<string,ConfiguratorProductOptions>());
    // get the product type
    const productType: ConfiguratorProductTypes|undefined = product.types.find(type => type.typeid===configuration.machineid);
    // get the quickcoupler system, if any
    const quickCouplerSystem: ConfiguratorProductQuickcouplers1|undefined = product.quickcouplers&&product.quickcouplers.find(q1 => q1.quickcouplers&&!!q1.quickcouplers.find(q2 => q2.quickcouplerid===configuration.quickcoupler));
    // get the quickcoupler model, if any
    const quickCouplerModel: ConfiguratorProductQuickcouplers|undefined = quickCouplerSystem&&quickCouplerSystem.quickcouplers.find(q2 => q2.quickcouplerid===configuration.quickcoupler);
    // get the quickcoupler buckets

    const quickCouplerBuckets: Array<ConfiguratorProductBuckets> = (quickCouplerModel&&quickCouplerModel.buckets&&configuration.buckets&&
        quickCouplerModel.buckets.filter(bucket => configuration.buckets.find(cb => cb===bucket.bucketid)))||Array<ConfiguratorProductBuckets>();
    return {
        quickCouplerSystemId: quickCouplerSystem&&quickCouplerSystem.quickcouplersystemid,
        quickCouplerModelId: configuration.quickcoupler,
        quickCouplerModelPrice: quickCouplerModel&&quickCouplerModel.price,
        quickCouplerBuckets,
        fullservice: configuration.fullservice && {
                ...configuration.fullservice
            },
        extendedwarranty: configuration.extendedwarranty && {
                ...configuration.extendedwarranty
            },
        options,
        //languagecode: language, TODO?
        //currency: currency, TODO?
        customerid: configuration.customerid,
        purchaseid: configuration.purchaseid,
        productformid: configuration.productformid,
        productid: configuration.productid,
        machineid: configuration.machineid,
        rent: configuration.rent && {
            ...configuration.rent
        },
        leasing: configuration.leasing && {
            ...configuration.leasing
        },
        price: configuration.price,
        cachedOptions,
        basePrice: productType && productType.price,
        standarddiscount: product.standarddiscount,
        specialdiscount: product.specialdiscount&&{...product.specialdiscount},
        divisionroundtonearest: product.divisionroundtonearest,
        syncState: {
            version: null,
            pending: false,
            failed: false
        },
        discount: configuration.discount||(product.specialdiscount?product.specialdiscount.percentage:product.standarddiscount)
    }
}

function computeConfigurationVersion(configuration: CurrentConfiguration | undefined): string {

    return md5(JSON.stringify(configuration?configuration:{}));
}

interface BasketAction
{
    type: ActionType.BASKET_CHANGEPRODUCTTYPE | ActionType.BASKET_CHANGEOPTIONSELECTION | ActionType.BASKET_CHANGELEASINGSELECTION |
        ActionType.BASKET_CHANGERENTSELECTION | ActionType.BASKET_CHANGEFULLSERVICESELECTION | ActionType.BASKET_CHANGEEXTENDEDWARRANTYSELECTION |
        ActionType.BASKET_CHANGEQUICKCOUPLERSELECTION | ActionType.BASKET_LOAD
    payload: object
}

export function basketProductTypeChange(type: ConfiguratorProductTypes): any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        await dispatch({ type: ActionType.BASKET_CHANGEPRODUCTTYPE, payload: { type } });
        await validateOrder(dispatch, getState, undefined);
    }
}

export function basketOptionSelectionChange(selected: boolean, category: ConfiguratorProductCategories, option: ConfiguratorProductOptions): any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        await dispatch({ type: ActionType.BASKET_CHANGEOPTIONSELECTION, payload: { selected: selected, category: category, option: option } });
        await validateOrder(dispatch, getState, undefined);
    }
}

export function basketLeasingSelectionChange(selected: boolean, durationmonths: number|undefined, initialpaymentpercent:number|undefined): any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        await dispatch({ type: ActionType.BASKET_CHANGELEASINGSELECTION, payload: { selected: selected, durationmonths: durationmonths, initialpaymentpercent: initialpaymentpercent } });
        await validateOrder(dispatch, getState, undefined);
    }
}

export function basketRentSelectionChange(selected: boolean, durationmonths: number|undefined) : any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        await dispatch({ type: ActionType.BASKET_CHANGERENTSELECTION, payload: { selected: selected, durationmonths: durationmonths } });
        await validateOrder(dispatch, getState, undefined);
    }
}

export function basketFullServiceSelectionChange(selected: boolean, durationmonths: number|undefined, hoursperyear: number|undefined): any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        await dispatch({ type: ActionType.BASKET_CHANGEFULLSERVICESELECTION, payload: { selected: selected, durationmonths: durationmonths, hoursperyear: hoursperyear } });
        await validateOrder(dispatch, getState, undefined);
    }
}

export function basketExtendedWarrantySelectionChange(selected: boolean, durationmonths: number|undefined, warrantytype: string|undefined ): any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        await dispatch({ type: ActionType.BASKET_CHANGEEXTENDEDWARRANTYSELECTION, payload: { selected: selected, durationmonths: durationmonths, warrantytype: warrantytype } });
        await validateOrder(dispatch, getState, undefined);
    }
}

export function basketQuickCouplerSelectionChange(systemId: number|undefined, modelId: number|undefined, modelPrice: ConfiguratorProductPrice|undefined, selected: boolean|undefined, bucket: ConfiguratorProductBuckets|undefined ): any {

    return async function(dispatch: ThunkDispatch<any,null,AnyAction>, getState: () => ApplicationState) {

        await dispatch({ type: ActionType.BASKET_CHANGEQUICKCOUPLERSELECTION, payload: { systemId: systemId, modelId: modelId, modelPrice: modelPrice, selected: selected, bucket: bucket } });
        await validateOrder(dispatch, getState, undefined);
    }
}

export function loadBasket(configuration: CurrentConfiguration, product: ConfiguratorProduct): BasketAction {
    return { type: ActionType.BASKET_LOAD, payload: { configuration, product } }
}


// WEBPACK FOOTER //
// ./src/basket.ts